import React, { ComponentProps } from 'react';
import { FullValidationErrorFragment } from '@common/graphql/fragments';
import { gql, useMutation, useQuery } from '@common/hooks';
import { Button, Divider, Drawer, Form } from 'antd';
import { StaffingRequestWithdrawModal } from 'works/legacy/pages/supplier/staffing-requests/staffing-request/staffing-request-withdraw-modal';
import {
  ButtonGroup,
  ErrorHandler,
  LoadingState,
  PostMessage,
  ShowComments,
  Tabs,
} from '@common/components';
import { SubmittalPacketsMainInput } from 'works/legacy/components/submittal-packets-main-input';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';
import { CreateWorksSubmittalPacket } from 'works/legacy/graphql/works-submittal-packet';
import { CommentInfoFragment, CommentInfoFragmentType } from '@common/graphql';

type Props = {
  staffingRequestApplicationId: string;
  onClose: () => void;
} & ComponentProps<typeof Drawer>;

type QueryType = {
  staffingRequestApplication: {
    id: string;
    worksProfile: {
      id: string;
      fullName: string;
      notArchivedWorksSubmittalPackets: {
        id: string;
        files: {
          id: string;
          createdAt: string;
          filename: string;
          downloadUrl: string;
          previewUrl: string;
        }[];
      }[];
    };
    staffingRequest: {
      id: string;
    };
    canWithdraw: {
      value: boolean;
    };
    activeOfferChangeRequest: {
      id: string;
      note: string;
      createdAt: string;
      worksMembership: {
        id: string;
        name: string;
      };
    };
    comments: CommentInfoFragmentType[];
  };
};

const DRAWER_QUERY = gql`
  query submittalPacketChangeResponseDrawer($applicationId: ID!) {
    staffingRequestApplication(id: $applicationId) {
      id
      staffingRequest {
        id
      }
      worksProfile {
        id
        fullName
        notArchivedWorksSubmittalPackets {
          id
          files {
            id
            createdAt
            downloadUrl
            filename
            previewUrl
          }
        }
      }
      state
      canWithdraw {
        value
      }
      activeOfferChangeRequest {
        id
        note
        createdAt
        worksMembership {
          id
          name
        }
      }
      comments {
        id
        ...CommentInfoFragment
      }
    }
  }
  ${CommentInfoFragment}
`;

const FORM_MUTATION = gql`
  mutation CreateResponseSubmittalPacketChange(
    $offerChangeRequestId: String!
    $input: CreateResponseSubmittalPacketChangeInput!
  ) {
    createResponseSubmittalPacketChange(
      offerChangeRequestId: $offerChangeRequestId
      input: $input
    ) {
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${FullValidationErrorFragment}
`;

export function StaffingRequestUpdateSubmissionDrawer({
  staffingRequestApplicationId,
  onClose,
  ...props
}: Props) {
  const [form] = Form.useForm();
  const isFormValid = !!Form.useWatch(['submittalPacket'], form);

  const { data, loading, error } = useQuery<QueryType>(DRAWER_QUERY, {
    variables: {
      applicationId: staffingRequestApplicationId,
    },
    onError: () => createMessage('error', 'Error occurred, try again later'),
  });

  const [submitResponse, { loading: submittingForm }] = useMutation(
    FORM_MUTATION,
    {
      onCompleted: (response) => {
        if (response.createResponseSubmittalPacketChange.errors) {
          addErrorsToForm(
            form,
            response.createResponseSubmittalPacketChange.errors
          );
        } else {
          createMessage('success', 'Change response has been submitted.');
          onClose();
        }
      },
      onError: () => {
        createMessage('error', 'Failed to perform action. Please try again.');
      },
      refetchQueries: [
        'SupplierJobPageCollectionQuery',
        'PaginatedCandidateSubmissionsQuery',
      ],
    }
  );

  const [createWorkSubmittalPacket, { error: createSubmittalPacketError }] =
    useMutation(CreateWorksSubmittalPacket);

  const getSubmittalPacketId = async () => {
    const { submittalPacket } = form.getFieldsValue();
    const submittalPacketId = submittalPacket?.target?.value;

    if (submittalPacketId) return submittalPacketId;

    const { data: packet } = await createWorkSubmittalPacket({
      variables: {
        worksProfileId: data?.staffingRequestApplication.worksProfile.id,
        file: submittalPacket?.file,
      },
    });

    return packet?.createWorksSubmittalPacket?.worksSubmittalPacket?.id;
  };

  const onSubmit = async () => {
    const worksSubmittalPacketId = await getSubmittalPacketId();

    submitResponse({
      variables: {
        offerChangeRequestId:
          data?.staffingRequestApplication.activeOfferChangeRequest?.id,
        input: {
          worksSubmittalPacketId,
        },
      },
    });
  };

  const commentsCount = data?.staffingRequestApplication.comments.length || 0;

  if (error || createSubmittalPacketError) {
    return (
      <ErrorHandler
        error={error}
        log="StaffingRequestUpdateSubmissionDrawer"
        messageType="contextual"
      />
    );
  }

  return (
    <>
      <Drawer
        placement="right"
        width={600}
        title={`${data?.staffingRequestApplication.worksProfile.fullName}'s submission`}
        footer={
          <ButtonGroup>
            {data?.staffingRequestApplication.canWithdraw.value && (
              <StaffingRequestWithdrawModal
                staffingRequestId={
                  data.staffingRequestApplication.staffingRequest.id
                }
                applicationId={data.staffingRequestApplication.id}
              />
            )}
            <Button
              size="large"
              type="primary"
              onClick={() => onSubmit()}
              disabled={!isFormValid}
              loading={submittingForm}
            >
              Continue
            </Button>
          </ButtonGroup>
        }
        onClose={onClose}
        {...props}
      >
        {!data || loading ? (
          <LoadingState />
        ) : (
          <Tabs defaultActiveKey="submission">
            <Tabs.Pane tab="Submission" key="submission">
              <section className="staffing-request-update-submission-drawer__pane-content">
                <h2>Change Request Note</h2>
                <PostMessage
                  name={
                    data.staffingRequestApplication.activeOfferChangeRequest
                      ?.worksMembership.name
                  }
                  message={
                    data.staffingRequestApplication.activeOfferChangeRequest
                      ?.note
                  }
                  time={
                    data.staffingRequestApplication.activeOfferChangeRequest
                      ?.createdAt
                  }
                />
                <Divider />
                <h2>Submittal Packet</h2>
                <Form layout="vertical" form={form}>
                  <Form.Item>
                    <SubmittalPacketsMainInput
                      disabled={submittingForm}
                      submittalPackets={
                        data.staffingRequestApplication.worksProfile
                          .notArchivedWorksSubmittalPackets
                      }
                    />
                  </Form.Item>
                </Form>
              </section>
            </Tabs.Pane>
            <Tabs.Pane
              tab={!commentsCount ? 'Notes' : `Notes (${commentsCount})`}
              key="notes"
            >
              <section className="staffing-request-update-submission-drawer__pane-content">
                <ShowComments
                  commentable={data.staffingRequestApplication}
                  render={(comment) => (
                    <PostMessage
                      message={comment.contentAsText}
                      time={comment.updatedAt}
                    />
                  )}
                  emptyRender={() => (
                    <PostMessage.Empty>
                      No notes to show from this candidate&apos;s application
                    </PostMessage.Empty>
                  )}
                />
              </section>
            </Tabs.Pane>
          </Tabs>
        )}
      </Drawer>
      <style jsx>{`
        .staffing-request-update-submission-drawer__pane-content {
          margin-top: 24px;
        }
      `}</style>
    </>
  );
}
